export function urlForRoute(route: {
  pathname: string
  query?: Record<string, string>
}) {
  const queryString = route.query
    ? `?${new URLSearchParams(route.query).toString()}`
    : ""
  return `${route.pathname}${queryString}`
}

function getTeamsPrefix(isAdminRef: boolean) {
  return isAdminRef ? "/admin/account/teams" : "/teams"
}

// Query param that carries solution-hub navigation context onto the shared
// playbook page. When the Solution Settings page links to a playbook it sets
// this to the installation uuid, so the playbook page can render the
// Hub > Settings > Playbook breadcrumb instead of the default Playbooks crumb.
export const SOLUTION_INSTALLATION_QUERY_PARAM = "solutionInstallation"

export const routes = {
  root: "/",
  default: "/default",
  profile: "/api/shared/accounts/profile",
  search: "/search",
  workspaceSetup: "/workspace-setup",
  auth: {
    base: "/auth",
    redirect: "/auth/redirect",
    signin: (workosOrgId?: string, trigger?: boolean) =>
      workosOrgId
        ? `/auth/signin?orgId=${workosOrgId}&trigger=${trigger}`
        : "/auth/signin",
    logout: "/auth/logout",
    signup: "/auth/signup",
    error: (context?: string, code?: string) => {
      const params = new URLSearchParams()
      if (context) params.set("context", context)
      if (code) params.set("code", code)
      return params.toString()
        ? `/auth/error?${params.toString()}`
        : "/auth/error"
    },
    callback: (returnPath: string = "/") => ({
      pathname: "/auth/callback",
      query: { returnPath },
    }),
  },
  workspaceSelect: "/workspace-select",
  onboarding: {
    slackChannels: "/onboarding/slack-channels",
    publicSlackChannels: "/slack/install/channels",
    publicSlackTenant: "/slack/install/tenant",
  },
  popup: {
    out: (url: string) => {
      return {
        pathname: "/popup/out",
        query: { url: url },
      }
    },
    in: (status: "success" | "failure", result: string = "") => ({
      pathname: "/popup/in",
      query: { status, result },
    }),
  },
  inbox: {
    list: "/inbox",
  },
  library: {
    list: "/library",
    detail: (documentId: string) => `/library/${documentId}`,
    presentationTemplate: (templateId: string) =>
      `/library/presentation-templates/${templateId}`,
    // The documents a Billing & Usage receipt line charged, cached under
    // `cacheKey` (POST /billing/object_receipt/documents); the page links back to billing.
    fromBilling: (cacheKey: string, title: string) =>
      `/library?${new URLSearchParams({ cache_key: cacheKey, source: "billing", title }).toString()}`,
  },
  folders: {
    // The Folders experience renders on the canonical /library route (there is
    // no bare /folders route). Folder detail lives at the /library subpath.
    list: "/library",
    detail: (folderId: string) => `/library/folders/${folderId}`,
  },
  agentHarness: {
    list: "/agent-harness",
    authorize: "/agent-harness/api/authorize",
    chat: "/agent-harness/api/chat",
    prepare: "/agent-harness/api/chat/prepare",
  },
  marketplace: {
    list: "/marketplace",
    detail: (identifier: string) =>
      `/marketplace/${identifier
        .split("/")
        .filter(Boolean)
        .map((segment) => encodeURIComponent(segment))
        .join("/")}`,
  },
  dashboard: "/library",
  dashboardFilteredByTemplate: (layoutUuid: string) => ({
    pathname: "/library",
    query: {
      filter: "all",
      layoutUuid,
    },
  }),
  dashboardDetail: (dashboardId: string) => `/library/${dashboardId}`,
  dashboardTemplateGallery: "/library?contentType=layout",
  dashboardTemplatePreview: (templateId: string) => `/library/${templateId}`,
  operatingMemory: {
    list: "/operating-memory",
  },
  okrs: {
    list: "/okrs",
  },
  goal: {
    // id can be either a UUID or a short ID (e.g., "OBJ-001")
    // Type is fetched from API, no query param needed
    detail: (id: string) => ({
      pathname: `/goal/${id}`,
    }),
  },
  initiatives: {
    list: "/initiatives",
  },
  trash: {
    list: "/trash",
  },
  // Any member of a credit tenant: their own receipt, outside /admin.
  myUsage: "/my-usage",
  views: {
    list: "/views",
    new: "/views/new",
    detail: (viewId: string, switchToTenant?: string) => ({
      pathname: `/views/${viewId}`,
      query: switchToTenant ? { switchToTenant } : undefined,
    }),
  },
  reports: {
    list: "/reports",
  },
  reportsV2: {
    list: "/reports-v2",
    new: "/reports-v2/new",
    preview: "/reports/preview",
    // The Auto-updating OKRs a Billing & Usage receipt charged, cached under
    // `cacheKey` (POST /billing/object_receipt/okrs); the page links back to billing.
    previewFromBilling: (cacheKey: string, title: string) =>
      `/reports/preview?${new URLSearchParams({ cache_key: cacheKey, source: "billing", title }).toString()}`,
    usersPreview: "/reports/users-preview",
    detail: (viewId: string, switchToTenant?: string) => ({
      pathname: `/reports-v2/${viewId}`,
      query: switchToTenant ? { switchToTenant } : undefined,
    }),
  },
  playbooks: {
    list: "/playbooks",
    detail: (uuid: string, tab?: string) =>
      tab ? `/playbooks/${uuid}?tab=${tab}` : `/playbooks/${uuid}`,
    // Playbook page reached from a solution's Settings page — carries the
    // installation uuid so the breadcrumb reflects the hub > settings path.
    detailFromSolution: (uuid: string, installationUuid: string) =>
      `/playbooks/${uuid}?${SOLUTION_INSTALLATION_QUERY_PARAM}=${installationUuid}`,
    // The playbooks a Billing & Usage receipt line charged runs of, cached under
    // `cacheKey` (POST /billing/object_receipt/playbooks); the page links back to billing.
    fromBilling: (cacheKey: string, title: string) =>
      `/playbooks?${new URLSearchParams({ cache_key: cacheKey, source: "billing", title }).toString()}`,
  },
  patterns: {
    list: "/patterns",
  },
  solutions: {
    installationSettings: (installationUuid: string) =>
      `/solutions/installations/${installationUuid}/settings`,
  },
  skills: {
    list: "/skills",
    new: "/skills/new",
    detail: (uuid: string) => `/skills/${uuid}`,
  },
  chat: {
    deepLink: (threadUuid: string) => `/chat?thread=${threadUuid}`,
  },
  chats: {
    list: "/chats",
    new: "/chats/new",
    detail: (threadKey: string) => `/chats/${threadKey}`,
  },
  connectors: {
    list: "/connectors",
  },
  admin: {
    root: "/admin",
    general: "/admin/account/general",
    earlyAccess: "/admin/account/early-access",
    usage: "/admin/account/usage",
    spending: "/admin/account/spending",
    users: "/admin/account/users",
    integrations: "/admin/account/integrations",
    hrisIntegrations: "/admin/account/hris-integrations",
    workdaySettings: "/admin/account/hris-integrations/workday",
    integrationConnections: (connectorUuid: string) =>
      `/admin/account/integrations/${encodeURIComponent(connectorUuid)}/connections`,
    collaborationPlatforms: "/admin/account/collaboration-platforms",
    slackSettings: "/admin/account/collaboration-platforms/slack",
    agentApps: "/admin/account/agent-apps",
    importOkrs: "/admin/okrs/data",
    teams: "/admin/account/teams",
    labels: "/admin/account/labels",
    billing: "/admin/account/billing",
    templates: "/admin/templates",
    documentTemplate: (id: string) =>
      `/admin/templates/document-templates/${id}`,
    presentationTemplate: (id: string) =>
      `/admin/templates/presentation-templates/${id}`,
    // Credit-billing tenants get one combined page in place of Billing + Usage.
    billingUsage: "/admin/account/billing-usage",
    billingLimits: "/admin/account/billing-usage/limits",
  },
  billing: {
    plans: "/admin/account/billing/plans",
  },
  labels: {
    preview: (labelName: string, type?: "document") => {
      // Suffix extracted to avoid a nested template literal (SonarCloud S4624).
      const typeParam = type ? `&type=${type}` : ""
      return `/admin/account/labels/preview?label=${encodeURIComponent(labelName)}${typeParam}`
    },
  },
  teams: {
    list: (isAdminRef: boolean) => getTeamsPrefix(isAdminRef),
    create: (isAdminRef: boolean, parentUuid?: string) =>
      parentUuid
        ? `${getTeamsPrefix(isAdminRef)}/create?parentUuid=${parentUuid}`
        : `${getTeamsPrefix(isAdminRef)}/create`,
    detail: (teamId: string, isAdminRef: boolean) =>
      `${getTeamsPrefix(isAdminRef)}/${teamId}`,
    settings: (teamId: string, isAdminRef: boolean) =>
      `${getTeamsPrefix(isAdminRef)}/${teamId}/settings`,
    members: (teamId: string, isAdminRef: boolean) =>
      `${getTeamsPrefix(isAdminRef)}/${teamId}/members`,
    checkIns: (teamId: string, isAdminRef: boolean) =>
      `${getTeamsPrefix(isAdminRef)}/${teamId}/check-ins`,
    archive: (teamId: string, isAdminRef: boolean) =>
      `${getTeamsPrefix(isAdminRef)}/${teamId}/archive`,
  },
  explorerView: {
    detail: (viewId: string, switchToTenant?: string) => {
      const query: Record<string, string> = { viewId }
      if (switchToTenant) query.switchToTenant = switchToTenant
      return { pathname: "/okrs", query }
    },
    viewDetail: (viewId: string, switchToTenant?: string) => ({
      pathname: `/views/${viewId}`,
      query: switchToTenant ? { switchToTenant } : undefined,
    }),
  },
} as const
